import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, BACKEND_URL } from '../App';
import { useAuth } from '../context/AuthContext';
import AuthModal from '../components/AuthModal';
import HowToPlayCard from '../components/HowToPlayCard';
import OnboardingModal from '../components/OnboardingModal';
import WinCelebrationModal from '../components/WinCelebrationModal';
import CardShop from '../components/CardShop';
import { useFuzzyRate, fmtFuzzyPerXrp } from '../hooks/useFuzzyRate';
import LoyaltyPill from '../components/LoyaltyPill';
import FireballLatest from '../components/FireballLatest';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Wallet, Trophy, DollarSign, LogOut, Link2, Send, ChevronRight,
  Plus, Flame, Gift, Shield, ShieldCheck, Info, CreditCard, Zap, Lock, Package,
  TrendingUp, Star, Crown, Gem, Award, ArrowUpRight, History, Gamepad2, Gavel, HelpCircle, Copy
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import VitaminCGainzLogo from '../components/VitaminCGainzLogo';
import ActivityTicker from '../components/ActivityTicker';
import DrawCountdown from '../components/DrawCountdown';
import { AuthenticityFields, uploadPhotos } from '../components/AuthenticityFields';
import { AuthenticityBadge } from '../components/AuthenticityBadge';
import { HostApplicationDialog } from '../components/HostApplicationDialog';
import { NftTrustBadge } from '../components/NftTrustBadge';
import NftPickerDialog from '../components/NftPickerDialog';
import ClaimWalletDialog from '../components/ClaimWalletDialog';
import XamanConnectDialog from '../components/XamanConnectDialog';
import { useFormPersistence } from '../hooks/useFormPersistence';
import { isNewListing, timeAgo } from '../utils/timeAgo';

const tg = () => window.Telegram?.WebApp;
const haptic = (type = 'impact', style = 'light') => {
  try {
    if (type === 'impact') tg()?.HapticFeedback?.impactOccurred(style);
    else if (type === 'notification') tg()?.HapticFeedback?.notificationOccurred(style);
  } catch (e) {}
};

const ROOM_ICONS = [Zap, Flame, Crown, Gem, Award];
const ROOM_COLORS = [
  { bg: 'from-slate-700/80 to-slate-800/80', accent: 'text-slate-300', border: 'border-slate-600/50', glow: '' },
  { bg: 'from-zinc-700/60 to-zinc-800/60', accent: 'text-zinc-200', border: 'border-zinc-500/40', glow: '' },
  { bg: 'from-amber-900/30 to-amber-950/40', accent: 'text-amber-300', border: 'border-amber-500/30', glow: 'shadow-amber-500/5 shadow-lg' },
  { bg: 'from-cyan-900/20 to-cyan-950/30', accent: 'text-cyan-300', border: 'border-cyan-400/30', glow: 'shadow-cyan-400/5 shadow-lg' },
  { bg: 'from-blue-900/30 to-indigo-950/40', accent: 'text-blue-300', border: 'border-blue-400/30', glow: 'shadow-blue-400/10 shadow-lg' },
];

function Home() {
  const { user, isAuthenticated, loading, logout, updateBalance, updateBalances, isTelegram } = useAuth();
  const [games, setGames] = useState([]);
  const [hostedGames, setHostedGames] = useState([]);
  const [feeConfig, setFeeConfig] = useState({ hosted_fee_waived: true, hosted_platform_fee_pct: 0, host_fee_pct: 0.05 });
  const fuzzyRate = useFuzzyRate(true);
  const [auctions, setAuctions] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showDepositDialog, setShowDepositDialog] = useState(false);
  const [onboardingForceOpen, setOnboardingForceOpen] = useState(false);
  const [drawIsUrgent, setDrawIsUrgent] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [showWalletLinkDialog, setShowWalletLinkDialog] = useState(false);
  const [showHostDialog, setShowHostDialog] = useState(false);
  const [showHostApplyDialog, setShowHostApplyDialog] = useState(false);
  // Home-feed Quick BIN
  const [quickBinGame, setQuickBinGame] = useState(null);
  const [quickBinDrawType, setQuickBinDrawType] = useState('midday');
  const [quickBinLoading, setQuickBinLoading] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [depositInfo, setDepositInfo] = useState(null);
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositTestnetAck, setDepositTestnetAck] = useState(false);
  const [recentDeposits, setRecentDeposits] = useState([]);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawUseAlt, setWithdrawUseAlt] = useState(false);
  const [withdrawAltAddress, setWithdrawAltAddress] = useState('');
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [withdrawSavedId, setWithdrawSavedId] = useState(''); // selected saved entry_id; '' = manual
  const [withdrawSaveNew, setWithdrawSaveNew] = useState(false);
  const [withdrawSaveLabel, setWithdrawSaveLabel] = useState('');
  const [withdrawCurrency, setWithdrawCurrency] = useState('XRP');
  const [actionLoading, setActionLoading] = useState(false);
  const [winners, setWinners] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [hostForm, setHostForm] = useState({
    room_name: '',
    entry_price: '',
    prize_type: 'nft',
    prize_description: '',
    prize_value_xrp: '',
    currency: 'XRP',
    bin_flex_currency: false,
    nft_token_id: '',
    physical_shipping_notes: '',
    min_players: 3,
    bin_price: '',
    condition: '',
    grade: '',
    cert_number: '',
  });
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoUrls, setPhotoUrls] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [certVerified, setCertVerified] = useState(null);

  // Auction creation
  const [showAuctionDialog, setShowAuctionDialog] = useState(false);
  const [auctionForm, setAuctionForm] = useState({
    room_name: '',
    prize_type: 'nft',
    prize_description: '',
    prize_value_xrp: '',
    starting_bid: '',
    reserve_price: '',
    duration_hours: 24,
    bid_increment: 1,
    nft_token_id: '',
    physical_shipping_notes: '',
    condition: '',
    grade: '',
    cert_number: '',
  });
  const [auctionPhotoFiles, setAuctionPhotoFiles] = useState([]);
  const [auctionPhotoUrls, setAuctionPhotoUrls] = useState([]);
  const [auctionCertVerified, setAuctionCertVerified] = useState(null);
  const [hostNftAnalysis, setHostNftAnalysis] = useState(null);
  const hostApprox = (amt) => {
    const n = parseFloat(amt);
    if (!fuzzyRate || !n) return null;
    return hostForm.currency === 'FUZZY'
      ? `≈ ${(n * fuzzyRate.xrp_per_fuzzy).toLocaleString(undefined, { maximumFractionDigits: 2 })} XRP`
      : `≈ ${Math.round(n * fuzzyRate.fuzzy_per_xrp).toLocaleString()} FUZZY`;
  };
  const [auctionNftAnalysis, setAuctionNftAnalysis] = useState(null);
  const [verifyingNft, setVerifyingNft] = useState(false);
  // NFT wallet picker (host/auction)
  const [showHostNftPicker, setShowHostNftPicker] = useState(false);
  const [showAuctionNftPicker, setShowAuctionNftPicker] = useState(false);
  // Wallet claim flow (triggered on 409 from /api/auth/link-wallet)
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [claimAddress, setClaimAddress] = useState('');
  // Xaman one-tap connect (primary wallet-linking method)
  const [showXamanDialog, setShowXamanDialog] = useState(false);
  const navigate = useNavigate();

  // Persist form drafts so navigating to Xaman doesn't wipe progress
  const userKey = user?.user_id || 'guest';
  const { clear: clearHostDraft } = useFormPersistence(
    `vcg.hostForm.${userKey}`, hostForm, setHostForm
  );
  const { clear: clearAuctionDraft } = useFormPersistence(
    `vcg.auctionForm.${userKey}`, auctionForm, setAuctionForm
  );

  useEffect(() => {
    fetchGames();
    fetchHostedGames();
    fetchAuctions();
    fetchWinners();
    fetchLeaderboard();
    axios.get(`${API}/fees`).then(r => setFeeConfig(r.data)).catch(() => {});
  }, []);

  const fetchGames = async () => {
    try {
      const response = await axios.get(`${API}/games`);
      setGames(response.data.games.filter(g => g.is_admin_room));
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  const fetchHostedGames = async () => {
    try {
      const response = await axios.get(`${API}/hosted-games`);
      setHostedGames(response.data.hosted_games || []);
    } catch (error) {
      console.error('Error fetching hosted games:', error);
    }
  };

  const fetchAuctions = async () => {
    try {
      const response = await axios.get(`${API}/auctions`);
      setAuctions(response.data.auctions || []);
    } catch (error) {
      console.error('Error fetching auctions:', error);
    }
  };

  const fetchWinners = async () => {
    try {
      const response = await axios.get(`${API}/winners`);
      setWinners(response.data.winners.slice(0, 5));
    } catch (error) {
      console.error('Error fetching winners:', error);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const handleLinkWallet = async () => {
    if (!walletAddress || !walletAddress.startsWith('r') || walletAddress.length < 25) {
      toast.error('Enter a valid XRP address');
      return;
    }
    setActionLoading(true);
    try {
      await axios.post(`${API}/auth/link-wallet`, { xrpl_address: walletAddress });
      haptic('notification', 'success');
      toast.success('Wallet linked!');
      setShowWalletLinkDialog(false);
      setWalletAddress('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Backend returns 409 with { code: 'wallet_already_linked', can_claim: true, xrpl_address } when stuck.
      // Pivot to the Claim Wallet flow instead of just toasting.
      if (error.response?.status === 409 && detail?.can_claim) {
        haptic('notification', 'warning');
        setClaimAddress(detail.xrpl_address || walletAddress);
        setShowWalletLinkDialog(false);
        setShowClaimDialog(true);
      } else {
        haptic('notification', 'error');
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to link wallet');
        toast.error(msg);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const fetchDepositInfo = async () => {
    setDepositLoading(true);
    try {
      const [infoRes, depositsRes] = await Promise.all([
        axios.get(`${API}/user/deposit-info`),
        axios.get(`${API}/user/deposits?limit=5`)
      ]);
      setDepositInfo(infoRes.data);
      setRecentDeposits(depositsRes.data.deposits || []);
    } catch (error) {
      toast.error('Failed to load deposit info');
    } finally {
      setDepositLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    const availBal = withdrawCurrency === 'FUZZY' ? (user?.fuzzy_balance || 0) : (user?.balance || 0);
    if (parseFloat(withdrawAmount) > availBal) {
      toast.error(`Insufficient ${withdrawCurrency} balance`);
      return;
    }
    // Resolve destination: saved entry > explicit alt > linked wallet
    let destination = null;
    let isManualAlt = false;
    if (withdrawUseAlt) {
      if (withdrawSavedId) {
        const picked = savedAddresses.find((a) => a.entry_id === withdrawSavedId);
        if (!picked) { toast.error('Saved address not found — refresh and try again'); return; }
        destination = picked.address;
      } else {
        const addr = (withdrawAltAddress || '').trim();
        if (!addr.startsWith('r') || addr.length < 25) {
          toast.error('Enter a valid XRPL address (starts with r…)');
          return;
        }
        destination = addr;
        isManualAlt = true;
      }
    } else if (!user?.xrpl_address) {
      toast.error('Link your XRP wallet first or enable "send to different address"');
      return;
    }

    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/user/withdraw`, {
        amount: parseFloat(withdrawAmount),
        currency: withdrawCurrency,
        ...(destination ? { destination } : {}),
      });
      updateBalances({ balance: response.data.new_balance, fuzzy_balance: response.data.new_fuzzy_balance });
      haptic('notification', 'success');
      toast.success(`Withdrew ${withdrawAmount} ${withdrawCurrency} — TX: ${response.data.tx_hash?.slice(0, 12)}...`);

      // Save the manual alt-address to the user's address book if requested
      if (isManualAlt && withdrawSaveNew && withdrawSaveLabel.trim()) {
        try {
          await axios.post(`${API}/user/address-book`, {
            label: withdrawSaveLabel.trim(),
            address: destination,
          });
          toast.success(`Saved as "${withdrawSaveLabel.trim()}" in your Address Book`);
        } catch (e) {
          // Don't block the withdrawal success — just inform.
          toast.info(e.response?.data?.detail || 'Withdrawal sent. Address not saved.');
        }
      }

      setShowWithdrawDialog(false);
      setWithdrawAmount('');
      setWithdrawUseAlt(false);
      setWithdrawAltAddress('');
      setWithdrawSavedId('');
      setWithdrawSaveNew(false);
      setWithdrawSaveLabel('');
    } catch (error) {
      haptic('notification', 'error');
      toast.error(error.response?.data?.detail || 'Withdraw failed');
    } finally {
      setActionLoading(false);
    }
  };

  const fetchSavedAddresses = async () => {
    if (!isAuthenticated) return;
    try {
      const res = await axios.get(`${API}/user/address-book`);
      setSavedAddresses(res.data.items || []);
    } catch { /* ignore */ }
  };

  const handleHostGame = async () => {
    const { room_name, entry_price, prize_type, prize_description, prize_value_xrp } = hostForm;
    if (!room_name || !entry_price || !prize_description || !prize_value_xrp) {
      toast.error('Fill all required fields');
      return;
    }
    // Physical items should have photos
    if (prize_type === 'physical' && photoFiles.length === 0 && photoUrls.length === 0) {
      toast.error('Upload at least 1 photo for physical items');
      return;
    }
    setActionLoading(true);
    try {
      // Upload photos first
      let allPhotoUrls = [...photoUrls];
      if (photoFiles.length > 0) {
        setUploadingPhotos(true);
        allPhotoUrls = await uploadPhotos(photoFiles, photoUrls);
        setUploadingPhotos(false);
      }

      const response = await axios.post(`${API}/hosted-games/create`, {
        room_name,
        entry_price: parseFloat(entry_price),
        prize_type,
        prize_description,
        prize_value_xrp: parseFloat(prize_value_xrp),
        nft_token_id: hostForm.nft_token_id || null,
        physical_shipping_notes: hostForm.physical_shipping_notes || null,
        min_players: parseInt(hostForm.min_players, 10) || 1,
        bin_price: hostForm.bin_price !== '' ? parseFloat(hostForm.bin_price) : null,
        currency: hostForm.currency || 'XRP',
        bin_flex_currency: !!hostForm.bin_flex_currency,
        condition: hostForm.condition || null,
        grade: hostForm.grade || null,
        cert_number: hostForm.cert_number || null,
        photo_urls: allPhotoUrls.length > 0 ? allPhotoUrls : null,
      });
      haptic('notification', 'success');
      toast.success(response.data.message);
      setShowHostDialog(false);
      setHostForm({ room_name: '', entry_price: '', prize_type: 'nft', prize_description: '', prize_value_xrp: '', currency: 'XRP', nft_token_id: '', physical_shipping_notes: '', min_players: 3, bin_price: '', condition: '', grade: '', cert_number: '' });
      setPhotoFiles([]);
      setPhotoUrls([]);
      setCertVerified(null);
      clearHostDraft();
      fetchHostedGames();
    } catch (error) {
      haptic('notification', 'error');
      toast.error(error.response?.data?.detail || 'Failed to create game');
    } finally {
      setActionLoading(false);
      setUploadingPhotos(false);
    }
  };

  const handleVerifyNft = async (tokenId, target) => {
    if (!tokenId || !user?.xrpl_address) {
      toast.error(user?.xrpl_address ? 'Enter a Token ID first' : 'Link your wallet first');
      return;
    }
    setVerifyingNft(true);
    try {
      const res = await axios.post(`${API}/verify/nft`, {
        nft_token_id: tokenId,
        host_wallet: user.xrpl_address,
      });
      if (target === 'host') setHostNftAnalysis(res.data);
      else setAuctionNftAnalysis(res.data);
      const level = res.data.risk_level;
      if (level === 'safe') toast.success('NFT verified — looks safe!');
      else if (level === 'caution') toast.warning('NFT has caution flags — review before listing');
      else toast.error('NFT failed safety check');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verify failed');
    } finally {
      setVerifyingNft(false);
    }
  };

  const handleCreateAuction = async () => {
    const { room_name, prize_type, prize_description, prize_value_xrp, starting_bid } = auctionForm;
    if (!room_name || !prize_description || !prize_value_xrp || !starting_bid) {
      toast.error('Fill all required fields');
      return;
    }
    if (prize_type === 'physical' && auctionPhotoFiles.length === 0 && auctionPhotoUrls.length === 0) {
      toast.error('Upload at least 1 photo for physical items');
      return;
    }
    setActionLoading(true);
    try {
      let allPhotoUrls = [...auctionPhotoUrls];
      if (auctionPhotoFiles.length > 0) {
        allPhotoUrls = await uploadPhotos(auctionPhotoFiles, auctionPhotoUrls);
      }
      const response = await axios.post(`${API}/auctions/create`, {
        room_name,
        prize_type,
        prize_description,
        prize_value_xrp: parseFloat(prize_value_xrp),
        starting_bid: parseFloat(starting_bid),
        reserve_price: auctionForm.reserve_price ? parseFloat(auctionForm.reserve_price) : null,
        duration_hours: parseInt(auctionForm.duration_hours, 10),
        bid_increment: parseFloat(auctionForm.bid_increment) || 1,
        nft_token_id: auctionForm.nft_token_id || null,
        physical_shipping_notes: auctionForm.physical_shipping_notes || null,
        condition: auctionForm.condition || null,
        grade: auctionForm.grade || null,
        cert_number: auctionForm.cert_number || null,
        photo_urls: allPhotoUrls.length > 0 ? allPhotoUrls : null,
      });
      haptic('notification', 'success');
      toast.success(response.data.message);
      setShowAuctionDialog(false);
      setAuctionForm({
        room_name: '', prize_type: 'nft', prize_description: '', prize_value_xrp: '',
        starting_bid: '', reserve_price: '', duration_hours: 24, bid_increment: 1,
        nft_token_id: '', physical_shipping_notes: '', condition: '', grade: '', cert_number: '',
      });
      setAuctionPhotoFiles([]);
      setAuctionPhotoUrls([]);
      setAuctionCertVerified(null);
      clearAuctionDraft();
      fetchAuctions();
    } catch (error) {
      haptic('notification', 'error');
      toast.error(error.response?.data?.detail || 'Failed to create auction');
    } finally {
      setActionLoading(false);
    }
  };

  const navigateToRoom = (game) => {
    haptic('impact', 'medium');
    if (!isAuthenticated) {
      toast.error('Please login first');
      if (!isTelegram) setShowAuthModal(true);
      return;
    }
    navigate(`/game/${game.game_id}`);
  };

  const openQuickBin = (game) => {
    haptic('impact', 'medium');
    if (!isAuthenticated) {
      toast.error('Please login first');
      if (!isTelegram) setShowAuthModal(true);
      return;
    }
    if (user?.user_id === game.host_user_id) {
      toast.info('You are the host of this room');
      return;
    }
    if (game.bin_available === false) {
      toast.info('BIN unavailable — slots already sold on both draws');
      return;
    }
    // Default to whichever draw has 0 sold slots (midday preferred if both open)
    const midOpen = (game.midday_slots_sold || 0) === 0;
    setQuickBinDrawType(midOpen ? 'midday' : 'evening');
    setQuickBinGame(game);
  };

  const handleQuickBin = async () => {
    if (!quickBinGame) return;
    setQuickBinLoading(true);
    try {
      const response = await axios.post(
        `${API}/games/${quickBinGame.game_id}/bin`,
        { draw_type: quickBinDrawType }
      );
      if (response.data.status === 'success') {
        haptic('notification', 'success');
        toast.success(response.data.message);
        updateBalance(response.data.new_balance);
        setQuickBinGame(null);
        fetchHostedGames();
      }
    } catch (error) {
      haptic('notification', 'error');
      toast.error(error.response?.data?.detail || 'BIN failed');
    } finally {
      setQuickBinLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500 mx-auto mb-3"></div>
          <p className="text-slate-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-6">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-slate-700/50 backdrop-blur-lg bg-slate-900/80">
        <div className="px-4 py-3">
          <div className="flex justify-between items-center">
            <VitaminCGainzLogo />
            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                {(user?.fuzzy_balance || 0) > 0 && (
                  <div data-testid="fuzzy-balance-display" className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <span className="text-xs">🍯</span>
                    <span className="text-xs font-semibold text-amber-300">{(user?.fuzzy_balance || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} FUZZY</span>
                  </div>
                )}
                <div data-testid="balance-display" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700">
                  <Wallet className="w-3.5 h-3.5 text-orange-400" />
                  <span className="text-sm font-semibold text-orange-300">{(user?.balance || 0).toFixed(2)} XRP</span>
                </div>
              </div>
            ) : (
              <Button
                data-testid="login-button"
                onClick={() => setShowAuthModal(true)}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-sm"
              >
                Login
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Activity Ticker */}
      <ActivityTicker />

      {/* Hero */}
      <section className="px-4 pt-6 pb-3 text-center">
        <h1 className="text-3xl sm:text-4xl font-black mb-1 tracking-tight">
          <span className="bg-gradient-to-r from-orange-400 via-amber-300 to-yellow-400 bg-clip-text text-transparent">
            Pick a Number.
          </span>
          <br />
          <span className="text-white">Win the Pool.</span>
        </h1>
        <p className="text-sm text-slate-500 mt-1">Illinois Pick 3 Fireball &middot; Midday &amp; Evening draws</p>
      </section>

      {/* Draw Countdown Timer */}
      <DrawCountdown />

      {/* Always-visible Ranks link (nav-tab surrogate for anon visitors) */}
      {!isAuthenticated && (
        <section className="px-4 pb-3">
          <button
            data-testid="leaderboard-anon-button"
            onClick={() => { haptic(); navigate('/leaderboard'); }}
            className="w-full flex items-center justify-between gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/15 px-3.5 py-2 transition-all"
          >
            <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-emerald-300">
              <Crown className="w-4 h-4" /> See top players
            </span>
            <ChevronRight className="w-4 h-4 text-emerald-400/70" />
          </button>
        </section>
      )}

      {/* Quick Actions */}
      {isAuthenticated && (
        <section className="px-4 pb-4">
          <div className="flex gap-2">
            <Button data-testid="deposit-button" onClick={() => { haptic(); setDepositTestnetAck(false); setShowDepositDialog(true); fetchDepositInfo(); }} size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-xs h-9">
              <DollarSign className="w-3.5 h-3.5 mr-1" /> Deposit
            </Button>
            <Button data-testid="withdraw-button" onClick={() => { haptic(); fetchSavedAddresses(); setShowWithdrawDialog(true); }} size="sm" variant="outline" className="flex-1 border-slate-600 text-slate-300 text-xs h-9">
              <Send className="w-3.5 h-3.5 mr-1" /> Withdraw
            </Button>
            <Button data-testid="link-wallet-button" onClick={() => { haptic(); setShowWalletLinkDialog(true); }} size="sm" variant="outline" className="border-slate-600 text-slate-300 text-xs h-9 px-3">
              <Link2 className="w-3.5 h-3.5" />
            </Button>
            {/* Sign-out visible everywhere (including Telegram) so the user can switch accounts. */}
            <Button data-testid="logout-button" variant="ghost" size="sm" onClick={() => { logout(); toast.info('Logged out'); }} className="text-slate-500 px-2 h-9">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="grid grid-cols-4 gap-2 mt-2">
            <Button
              data-testid="my-games-button"
              onClick={() => { haptic(); navigate('/my-games'); }}
              size="sm"
              variant="outline"
              className="border-purple-500/30 text-purple-300 text-xs h-8 px-1"
            >
              <Gamepad2 className="w-3.5 h-3.5 mr-1" /> My Games
            </Button>
            <Button
              data-testid="transactions-button"
              onClick={() => { haptic(); navigate('/transactions'); }}
              size="sm"
              variant="outline"
              className="border-slate-600 text-slate-300 text-xs h-8 px-1"
            >
              <History className="w-3.5 h-3.5 mr-1" /> History
            </Button>
            <Button
              data-testid="draws-button"
              onClick={() => { haptic(); navigate('/draws'); }}
              size="sm"
              variant="outline"
              className="border-amber-500/30 text-amber-300 text-xs h-8 px-1"
            >
              <Trophy className="w-3.5 h-3.5 mr-1" /> Draws
            </Button>
            <Button
              data-testid="leaderboard-button"
              onClick={() => { haptic(); navigate('/leaderboard'); }}
              size="sm"
              variant="outline"
              className="border-emerald-500/30 text-emerald-300 text-xs h-8 px-1"
            >
              <Crown className="w-3.5 h-3.5 mr-1" /> Ranks
            </Button>
          </div>
          <div className="flex justify-between items-center mt-1.5">
            <button
              data-testid="how-it-works-button"
              onClick={() => { haptic(); navigate('/guide'); }}
              className="text-[10px] text-orange-400/80 hover:text-orange-300 transition-colors inline-flex items-center gap-1"
            >
              <HelpCircle className="w-3 h-3" /> How it works
            </button>
            <button
              data-testid="settings-button"
              onClick={() => { haptic(); navigate('/settings'); }}
              className="text-[10px] text-slate-500 hover:text-slate-300 transition-colors"
            >
              Notification Settings
            </button>
          </div>
        </section>
      )}

      {/* How to Play — permanent, collapsible instructional card */}
      <section className="px-4 pb-4 space-y-3">
        <FireballLatest onUrgentChange={setDrawIsUrgent} />
        <LoyaltyPill />
        <HowToPlayCard
          isLoggedIn={!!user}
          onOpenModal={() => setOnboardingForceOpen(true)}
          onLogin={() => setShowAuthModal(true)}
          onDeposit={() => { haptic(); setDepositTestnetAck(false); setShowDepositDialog(true); fetchDepositInfo(); }}
        />
      </section>


      {/* XRP Pool Rooms */}
      <section className="px-4 pb-2">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-orange-500/15 border border-orange-500/25 flex items-center justify-center">
              <Flame className="w-4 h-4 text-orange-400" />
            </div>
            <div>
              <h3 data-testid="xrp-rooms-heading" className="text-base font-bold text-white leading-tight" style={{ scrollMarginTop: '96px' }}>XRP Pool Rooms</h3>
              <p className="text-[10px] text-slate-500">Pick a slot, win the entire pool</p>
            </div>
          </div>
          <span className="text-[10px] text-slate-600 bg-slate-800/60 px-2 py-1 rounded-full">{games.length} rooms</span>
        </div>
        <div className="space-y-2.5">
          {games.map((game, idx) => {
            const color = ROOM_COLORS[idx] || ROOM_COLORS[0];
            const Icon = ROOM_ICONS[idx] || ROOM_ICONS[0];
            return (
              <div
                key={game.game_id}
                data-testid={`game-room-${game.room_name.replace(/\s+/g, '-').toLowerCase()}`}
                className={`relative overflow-hidden rounded-2xl bg-gradient-to-r ${color.bg} border ${color.border} ${color.glow} cursor-pointer active:scale-[0.98] transition-all duration-150`}
                onClick={() => navigateToRoom(game)}
              >
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3.5">
                    <div className={`w-11 h-11 rounded-xl bg-black/20 flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${color.accent}`} />
                    </div>
                    <div>
                      <p className="font-bold text-white text-[15px] leading-tight">{game.room_name}{game.currency === 'FUZZY' ? ' 🍯' : ''}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {game.price_mode === 'first_buyer' && !game.total_cost ? (
                          <span className="text-[11px] text-amber-300 font-semibold">
                            First buyer sets the FUZZY price
                            {fuzzyRate && (
                              <span data-testid="home-fuzzy-rate" className="text-[10px] text-slate-400 font-normal ml-1.5">
                                · {fmtFuzzyPerXrp(fuzzyRate)} <span className="text-emerald-400">●</span> live
                              </span>
                            )}
                          </span>
                        ) : (
                          <>
                            <span className="text-[11px] text-slate-400">
                              Entry <span className="text-orange-400 font-semibold">{game.total_cost} {game.currency === 'FUZZY' ? 'FUZZY' : 'XRP'}</span>
                            </span>
                            <span className="text-[11px] text-slate-400">
                              Pool <span className="text-emerald-400 font-semibold">{game.entry_price * 10} {game.currency === 'FUZZY' ? 'FUZZY' : 'XRP'}</span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-500" />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Prize Rooms (User-Hosted) */}
      <section className="px-4 pt-5 pb-2">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-purple-500/15 border border-purple-500/25 flex items-center justify-center">
              <Gift className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <h3 data-testid="prize-rooms-heading" className="text-base font-bold text-white leading-tight">Prize Rooms</h3>
              <p className="text-[10px] text-slate-500">NFTs &amp; more</p>
            </div>
          </div>
          {isAuthenticated && (
            <div className="flex items-center gap-1.5">
              <Button
                data-testid="auction-create-button"
                onClick={() => { haptic(); setShowAuctionDialog(true); }}
                size="sm"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-[11px] h-7 px-3 rounded-full"
              >
                <Gavel className="w-3 h-3 mr-1" /> Auction
              </Button>
              <Button
                data-testid="host-game-button"
                onClick={() => { haptic(); (user?.can_host || feeConfig.open_hosting) ? setShowHostDialog(true) : setShowHostApplyDialog(true); }}
                size="sm"
                className="bg-purple-600 hover:bg-purple-700 text-[11px] h-7 px-3 rounded-full"
              >
                <Plus className="w-3 h-3 mr-1" /> Host
              </Button>
            </div>
          )}
        </div>
        {hostedGames.length === 0 && auctions.length === 0 ? (
          <div className="text-center py-8 bg-slate-800/30 border border-dashed border-slate-700/50 rounded-2xl">
            <Gift className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No prize rooms yet</p>
            <p className="text-[11px] text-slate-600 mt-0.5">Be the first to host — you keep the pool from every slot sold!</p>
            {isAuthenticated && (
              <Button
                data-testid="host-game-empty-cta"
                onClick={() => (user?.can_host || feeConfig.open_hosting) ? setShowHostDialog(true) : setShowHostApplyDialog(true)}
                size="sm"
                variant="outline"
                className="mt-3 border-purple-500/40 text-purple-300 text-xs h-8"
              >
                <Plus className="w-3 h-3 mr-1" /> List Your Prize
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2.5">
            {/* Active Auctions */}
            {auctions.map((auction) => (
              <div
                key={auction.auction_id}
                data-testid={`auction-${auction.auction_id.slice(0, 8)}`}
                className="rounded-2xl bg-gradient-to-r from-indigo-900/20 to-purple-900/15 border border-indigo-500/25 cursor-pointer active:scale-[0.98] transition-all duration-150 relative overflow-hidden"
                onClick={() => { haptic('impact', 'medium'); navigate(`/auction/${auction.auction_id}`); }}
              >
                <div className="absolute top-2 right-2 flex gap-1">
                  {isNewListing(auction.created_at) && (
                    <span data-testid="auction-new-badge" className="text-[8px] px-1.5 py-0.5 rounded-full bg-emerald-500/25 text-emerald-200 border border-emerald-500/40 font-black tracking-wider">NEW</span>
                  )}
                  <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30 font-black tracking-wider">AUCTION</span>
                </div>
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {auction.photo_urls && auction.photo_urls.length > 0 ? (
                      <img
                        data-testid={`auction-thumb-${auction.auction_id.slice(0, 8)}`}
                        src={auction.photo_urls[0].startsWith('http') ? auction.photo_urls[0] : `${BACKEND_URL}${auction.photo_urls[0]}`}
                        alt={auction.room_name}
                        className="w-12 h-12 rounded-xl object-cover border border-indigo-500/30 flex-shrink-0"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center flex-shrink-0">
                        <Gavel className="w-5 h-5 text-indigo-300" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-white text-sm leading-tight truncate">{auction.room_name}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-1">{auction.prize_description}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 font-medium uppercase">{auction.prize_type}</span>
                        <span className="text-[11px] text-slate-400">
                          {auction.current_bid > 0
                            ? <><span className="text-orange-400 font-semibold">{auction.current_bid.toFixed(1)} XRP</span> ({auction.bid_count} bids)</>
                            : <>Starts at <span className="text-orange-400 font-semibold">{auction.starting_bid} XRP</span></>
                          }
                        </span>
                      </div>
                      {(auction.condition || auction.cert_number) && (
                        <div className="mt-1.5">
                          <AuthenticityBadge
                            condition={auction.condition}
                            grade={auction.grade}
                            certNumber={auction.cert_number}
                            verified={auction.authenticity_verified}
                          />
                        </div>
                      )}
                      {auction.prize_type === 'nft' && auction.nft_analysis && (
                        <div className="mt-1">
                          <NftTrustBadge analysis={auction.nft_analysis} />
                        </div>
                      )}
                      {(auction.host_location || auction.created_at) && (
                        <div className="flex items-center gap-2 mt-1 text-[9px] text-slate-500">
                          {auction.host_location && <span>📍 {auction.host_location}</span>}
                          {auction.created_at && <span>· {timeAgo(auction.created_at)}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
                </div>
              </div>
            ))}
            {/* Hosted Games (slot-based) */}
            {hostedGames.map((game) => (
              <div
                key={game.game_id}
                data-testid={`hosted-room-${game.game_id.slice(0, 8)}`}
                className="rounded-2xl bg-gradient-to-r from-purple-900/15 to-slate-800/60 border border-purple-500/20 cursor-pointer active:scale-[0.98] transition-all duration-150 relative"
                onClick={() => navigateToRoom(game)}
              >
                {isNewListing(game.created_at) && (
                  <span data-testid={`hosted-new-${game.game_id.slice(0,8)}`} className="absolute top-2 right-2 text-[8px] px-1.5 py-0.5 rounded-full bg-emerald-500/25 text-emerald-200 border border-emerald-500/40 font-black tracking-wider">NEW</span>
                )}
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {game.photo_urls && game.photo_urls.length > 0 ? (
                      <img
                        data-testid={`hosted-thumb-${game.game_id.slice(0, 8)}`}
                        src={game.photo_urls[0].startsWith('http') ? game.photo_urls[0] : `${BACKEND_URL}${game.photo_urls[0]}`}
                        alt={game.room_name}
                        className="w-12 h-12 rounded-xl object-cover border border-purple-500/30 flex-shrink-0"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center flex-shrink-0">
                        {game.prize_type === 'nft' ? <Star className="w-4.5 h-4.5 text-purple-300" /> : <Package className="w-4.5 h-4.5 text-purple-300" />}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-white text-sm leading-tight truncate">{game.room_name}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5 line-clamp-1">{game.prize_description}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300 font-medium uppercase">{game.prize_type}</span>
                        <span className="text-[11px] text-slate-400">Entry <span className="text-orange-400 font-semibold">{game.total_cost} XRP</span></span>
                        {game.host_trusted && (
                          <span
                            data-testid={`hosted-trusted-${game.game_id.slice(0,8)}`}
                            className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-300 font-bold uppercase tracking-wide border border-sky-500/30"
                            title="Trusted Host — verified by admin"
                          >
                            <ShieldCheck className="w-2.5 h-2.5" /> Trusted
                          </span>
                        )}
                      </div>
                      {(game.condition || game.cert_number) && (
                        <div className="mt-1.5">
                          <AuthenticityBadge
                            condition={game.condition}
                            grade={game.grade}
                            certNumber={game.cert_number}
                            verified={game.authenticity_verified}
                          />
                        </div>
                      )}
                      {game.prize_type === 'nft' && game.nft_analysis && (
                        <div className="mt-1">
                          <NftTrustBadge analysis={game.nft_analysis} />
                        </div>
                      )}
                      {(game.host_location || game.created_at) && (
                        <div className="flex items-center gap-2 mt-1 text-[9px] text-slate-500">
                          {game.host_location && <span>📍 {game.host_location}</span>}
                          {game.created_at && <span>· {timeAgo(game.created_at)}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Right side: BIN pill for non-hosts, ChevronRight for hosts */}
                  {user?.user_id !== game.host_user_id ? (
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {(() => {
                        const binLocked = game.bin_available === false;
                        const middayOpen = (game.midday_slots_sold || 0) === 0;
                        const eveningOpen = (game.evening_slots_sold || 0) === 0;
                        const onlyOneLeft = !binLocked && (middayOpen !== eveningOpen);
                        const openLabel = middayOpen ? 'midday' : 'evening';
                        return (
                          <>
                            <button
                              data-testid={`home-bin-${game.game_id.slice(0, 8)}`}
                              data-bin-locked={binLocked ? 'true' : 'false'}
                              onClick={(e) => { e.stopPropagation(); openQuickBin(game); }}
                              disabled={binLocked}
                              title={binLocked ? 'BIN unavailable — slots already sold' : 'Buy It Now'}
                              className={`relative overflow-hidden rounded-lg px-2.5 py-1.5 transition-all ${
                                binLocked
                                  ? 'border border-slate-700 bg-slate-800/40 opacity-60 cursor-not-allowed'
                                  : 'border border-red-500/50 bg-gradient-to-r from-red-500/15 to-orange-500/15 hover:from-red-500/25 hover:to-orange-500/25 active:scale-95'
                              }`}
                            >
                              <div className="flex flex-col items-center leading-none">
                                <span className={`text-[9px] font-black tracking-wider ${binLocked ? 'text-slate-500' : 'text-red-300'}`}>BIN</span>
                                <span className={`text-[11px] font-bold mt-0.5 ${binLocked ? 'text-slate-500 line-through' : 'text-orange-300'}`}>
                                  {(game.bin_price ?? game.total_cost * 10).toFixed(1)}
                                </span>
                              </div>
                            </button>
                            {onlyOneLeft && (
                              <span
                                data-testid={`bin-scarcity-${game.game_id.slice(0, 8)}`}
                                className="text-[8px] font-bold text-amber-300 tracking-wide animate-pulse leading-none"
                              >
                                Only {openLabel} left
                              </span>
                            )}
                          </>
                        );
                      })()}
                      <ChevronRight className="w-4 h-4 text-slate-600" />
                    </div>
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Card Shop — fixed-price direct sales */}
      <CardShop />

      {/* Recent Winners */}
      {winners.length > 0 && (
        <section className="px-4 pt-5">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-yellow-400" />
            <h3 data-testid="winners-heading" className="text-base font-bold text-white">Recent Winners</h3>
          </div>
          <div className="space-y-1.5">
            {winners.map((winner, idx) => (
              <div key={`winner-${winner.username || 'ghost'}-${idx}`} data-testid={`winner-${idx}`} className="flex items-center justify-between bg-slate-800/40 border border-slate-700/50 rounded-xl px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-500/15 border border-indigo-500/25 text-indigo-300 text-sm font-bold">
                    {winner.winner_slot}
                  </span>
                  <span className="text-sm text-slate-300">{winner.winner_username || 'Anon'}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-semibold text-emerald-400">{(winner.pool_amount || 0).toFixed(1)} XRP</span>
                  <span className="text-[10px] text-slate-500 ml-2 capitalize">{winner.draw_type}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Leaderboard / Hall of Fame */}
      {leaderboard && (leaderboard.top_winners?.length > 0 || leaderboard.big_wins?.length > 0) && (
        <section className="px-4 pt-6">
          <div className="rounded-2xl overflow-hidden border border-amber-500/20 bg-gradient-to-br from-amber-950/20 via-slate-900/50 to-indigo-950/20">
            <div className="px-4 pt-4 pb-3">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-amber-500/15 border border-amber-500/25 flex items-center justify-center">
                  <Crown className="w-4 h-4 text-amber-400" />
                </div>
                <h3 data-testid="leaderboard-heading" className="text-base font-bold text-white">Hall of Fame</h3>
              </div>

              {/* Big Wins */}
              {leaderboard.big_wins?.length > 0 && (
                <div className="mb-4">
                  <p className="text-[10px] text-amber-400/70 font-semibold uppercase tracking-wider mb-2">Biggest Wins</p>
                  <div className="space-y-1.5">
                    {leaderboard.big_wins.slice(0, 3).map((win, idx) => (
                      <div key={`bigwin-${win.username}-${win.amount}-${idx}`} data-testid={`big-win-${idx}`} className="flex items-center justify-between bg-black/20 rounded-xl px-3 py-2">
                        <div className="flex items-center gap-2.5">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-black ${
                            idx === 0 ? 'bg-amber-500/25 text-amber-300 ring-1 ring-amber-500/40' :
                            idx === 1 ? 'bg-slate-500/25 text-slate-300' :
                            'bg-orange-500/15 text-orange-300'
                          }`}>
                            {idx + 1}
                          </span>
                          <div>
                            <p className="text-xs font-semibold text-white">{win.username}</p>
                            <p className="text-[9px] text-slate-500">{win.game_name} &middot; {win.draw_type}</p>
                          </div>
                        </div>
                        <span className="text-sm font-bold text-emerald-400">{win.amount} XRP</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Winners */}
              {leaderboard.top_winners?.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] text-indigo-400/70 font-semibold uppercase tracking-wider mb-2">All-Time Top Winners</p>
                  <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                    {leaderboard.top_winners.slice(0, 5).map((w, idx) => (
                      <div key={`topwinner-${w.username}-${idx}`} data-testid={`top-winner-${idx}`} className="flex-shrink-0 bg-indigo-500/5 border border-indigo-500/15 rounded-xl px-3 py-2 text-center min-w-[90px]">
                        <p className="text-xs font-bold text-white truncate">{w.username}</p>
                        <p className="text-[11px] font-semibold text-emerald-400 mt-0.5">{w.total_won} XRP</p>
                        <p className="text-[9px] text-slate-500">{w.wins_count} wins</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Hosts */}
              {leaderboard.top_hosts?.length > 0 && (
                <div>
                  <p className="text-[10px] text-purple-400/70 font-semibold uppercase tracking-wider mb-2">Top Hosts</p>
                  <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                    {leaderboard.top_hosts.slice(0, 5).map((h, idx) => (
                      <div key={`tophost-${h.username}-${idx}`} data-testid={`top-host-${idx}`} className="flex-shrink-0 bg-purple-500/5 border border-purple-500/15 rounded-xl px-3 py-2 text-center min-w-[90px]">
                        <p className="text-xs font-bold text-white truncate">{h.username}</p>
                        <p className="text-[11px] font-semibold text-purple-300 mt-0.5">{h.games_hosted} games</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Info Tabs at Bottom */}
      <section className="px-4 pt-8 pb-4">
        <Tabs defaultValue="howto" className="w-full">
          <TabsList data-testid="info-tabs-list" className="w-full bg-slate-800/60 border border-slate-700/50 rounded-xl h-auto p-1 gap-1">
            <TabsTrigger value="howto" className="flex-1 text-[11px] py-2 rounded-lg data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300 data-[state=active]:shadow-none text-slate-400">
              How It Works
            </TabsTrigger>
            <TabsTrigger value="wallets" className="flex-1 text-[11px] py-2 rounded-lg data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300 data-[state=active]:shadow-none text-slate-400">
              Wallets
            </TabsTrigger>
          </TabsList>

          {/* Earn Banner → links to dedicated page */}
          <div
            data-testid="earn-banner"
            onClick={() => navigate('/host-info')}
            className="mt-3 bg-gradient-to-r from-orange-500/10 to-amber-500/5 border border-orange-500/20 rounded-xl p-3.5 cursor-pointer active:scale-[0.98] transition-all"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-orange-500/15 flex items-center justify-center">
                  <TrendingUp className="w-4.5 h-4.5 text-orange-400" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">Host & Keep the Pool</p>
                  <p className="text-[10px] text-slate-500">List prizes, earn passive XRP</p>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-500" />
            </div>
          </div>

          <TabsContent value="howto" data-testid="tab-howto" className="mt-3">
            <div className="bg-slate-800/30 border border-slate-700/40 rounded-2xl p-4 space-y-4">
              <div className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-orange-500/15 text-orange-400 text-xs font-bold flex-shrink-0">1</span>
                <div>
                  <p className="text-sm font-semibold text-white">Choose a Room</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">XRP Pool rooms or Prize rooms — each has Midday &amp; Evening draws</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-orange-500/15 text-orange-400 text-xs font-bold flex-shrink-0">2</span>
                <div>
                  <p className="text-sm font-semibold text-white">Pick a Slot (0-9)</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Each slot maps to a single digit. Only 10 players per draw.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-orange-500/15 text-orange-400 text-xs font-bold flex-shrink-0">3</span>
                <div>
                  <p className="text-sm font-semibold text-white">Illinois Pick 3 Fireball Draws</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Midday ~12:40 PM CT &amp; Evening ~9:22 PM CT. 3rd line Fireball number decides the winner.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-500/15 text-emerald-400 text-xs font-bold flex-shrink-0">4</span>
                <div>
                  <p className="text-sm font-semibold text-white">Match = Win the Entire Pool</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">If your number matches, you win all the XRP in the pool. Winnings are credited instantly.</p>
                </div>
              </div>
              <div className="mt-2 pt-3 border-t border-slate-700/40">
                <p className="text-[10px] text-slate-500">
                  Admin XRP rooms require minimum 10 players. If not met, all entries are auto-refunded. Prize rooms start from 1 player.
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="wallets" data-testid="tab-wallets" className="mt-3">
            <div className="space-y-2.5">
              <p className="text-[11px] text-slate-400 mb-3">
                Connect one of these XRP-compatible wallets to deposit, withdraw, and receive winnings.
              </p>
              <WalletCard
                name="Xaman (XUMM)"
                desc="Most popular XRP wallet. Mobile-first, easy to use."
                tag="Recommended"
                tagColor="bg-emerald-500/15 text-emerald-300 border-emerald-500/25"
              />
              <WalletCard
                name="Trust Wallet"
                desc="Multi-chain wallet with XRP support. iOS & Android."
                tag="Popular"
                tagColor="bg-blue-500/15 text-blue-300 border-blue-500/25"
              />
              <WalletCard
                name="Ledger"
                desc="Hardware wallet for maximum security. Supports XRP natively."
                tag="Hardware"
                tagColor="bg-amber-500/15 text-amber-300 border-amber-500/25"
              />
              <WalletCard
                name="Tangem"
                desc="Card-shaped hardware wallet. Tap to sign XRP transactions."
                tag="Card"
                tagColor="bg-purple-500/15 text-purple-300 border-purple-500/25"
              />
              <div className="mt-3 bg-slate-800/30 border border-slate-700/40 rounded-xl p-3">
                <p className="text-[10px] text-slate-500 leading-relaxed">
                  Any wallet that supports the XRP Ledger (XRPL) is compatible. Never share your secret keys. Always verify the deposit address before sending.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </section>

      {/* Auth Modal */}
      {!isTelegram && (
        <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
      )}
      <OnboardingModal forceOpen={onboardingForceOpen} defer={drawIsUrgent} onClose={() => setOnboardingForceOpen(false)} />
      <WinCelebrationModal />

      {/* Host Game Dialog */}
      <HostApplicationDialog
        open={showHostApplyDialog}
        onOpenChange={setShowHostApplyDialog}
        onApproved={() => { setShowHostApplyDialog(false); setShowHostDialog(true); }}
      />
      <Dialog open={showHostDialog} onOpenChange={setShowHostDialog}>
        <DialogContent data-testid="host-game-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Host a Prize Room</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
            List your NFT or physical item for free. You keep the full pool of slot sales (minus the 5.89% platform fee) — the winner gets your prize.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              data-testid="host-room-name"
              placeholder="Room name (e.g. Gold Charizard)"
              value={hostForm.room_name}
              onChange={(e) => setHostForm(f => ({ ...f, room_name: e.target.value }))}
              className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
            />
            <Select value={hostForm.prize_type} onValueChange={(v) => setHostForm(f => ({ ...f, prize_type: v }))}>
              <SelectTrigger data-testid="host-prize-type" className="bg-slate-800 border-slate-700 text-white h-10 text-sm">
                <SelectValue placeholder="Prize type" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="nft" className="text-white">NFT</SelectItem>
                <SelectItem value="physical" className="text-white">Physical Item</SelectItem>
              </SelectContent>
            </Select>
            <Input
              data-testid="host-prize-description"
              placeholder="Prize description"
              value={hostForm.prize_description}
              onChange={(e) => setHostForm(f => ({ ...f, prize_description: e.target.value }))}
              className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
            />
            {/* Flip the Switch — accept FUZZY instead of XRP for this listing */}
            {fuzzyRate && (
              <p data-testid="host-live-rate" className="text-[10px] text-center text-cyan-300/90 bg-cyan-500/10 border border-cyan-500/20 rounded-lg px-2.5 py-1.5">
                ⇄ Live DEX rate: 1 XRP ≈ <b>{Math.round(fuzzyRate.fuzzy_per_xrp).toLocaleString()} FUZZY</b> · 100k FUZZY ≈ <b>{(100000 * fuzzyRate.xrp_per_fuzzy).toLocaleString(undefined, { maximumFractionDigits: 2 })} XRP</b> <span className="text-emerald-400">●</span>
              </p>
            )}
            <button
              type="button"
              data-testid="flip-the-switch"
              onClick={() => setHostForm(f => ({ ...f, currency: f.currency === 'FUZZY' ? 'XRP' : 'FUZZY' }))}
              className={`w-full flex items-center justify-between rounded-xl border p-3 transition-all ${
                hostForm.currency === 'FUZZY'
                  ? 'bg-amber-500/10 border-amber-500/40'
                  : 'bg-slate-800/50 border-slate-700'
              }`}
            >
              <div className="text-left">
                <p className={`text-xs font-bold ${hostForm.currency === 'FUZZY' ? 'text-amber-300' : 'text-white'}`}>
                  {hostForm.currency === 'FUZZY' ? '🍯 Flipped! Paid in FUZZY' : 'Flip the Switch'}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {hostForm.currency === 'FUZZY'
                    ? 'Buyers pay slots in FUZZY memecoin — your earnings arrive in FUZZY'
                    : 'Accept FUZZY memecoin per slot instead of XRP'}
                </p>
              </div>
              <div className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors ${hostForm.currency === 'FUZZY' ? 'bg-amber-500' : 'bg-slate-700'}`}>
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${hostForm.currency === 'FUZZY' ? 'left-[22px]' : 'left-0.5'}`} />
              </div>
            </button>
            {/* Open BIN & Offers to both currencies — buyer flips the switch at checkout */}
            <button
              type="button"
              data-testid="bin-flex-toggle"
              onClick={() => setHostForm(f => ({ ...f, bin_flex_currency: !f.bin_flex_currency }))}
              className={`w-full flex items-center justify-between rounded-xl border p-3 transition-all ${
                hostForm.bin_flex_currency
                  ? 'bg-cyan-500/10 border-cyan-500/40'
                  : 'bg-slate-800/50 border-slate-700'
              }`}
            >
              <div className="text-left">
                <p className={`text-xs font-bold ${hostForm.bin_flex_currency ? 'text-cyan-300' : 'text-white'}`}>
                  {hostForm.bin_flex_currency ? '⇄ BIN & Offers: buyer picks the coin' : 'Open BIN & Offers to both coins'}
                </p>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {hostForm.bin_flex_currency
                    ? `Buy It Now & offers can be paid in XRP or FUZZY (live DEX rate) — slots stay in ${hostForm.currency}`
                    : `Let BIN/offer buyers flip the switch and pay in either XRP or FUZZY`}
                </p>
              </div>
              <div className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors ${hostForm.bin_flex_currency ? 'bg-cyan-500' : 'bg-slate-700'}`}>
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${hostForm.bin_flex_currency ? 'left-[22px]' : 'left-0.5'}`} />
              </div>
            </button>
            <div className="grid grid-cols-2 gap-2">
              <Input
                data-testid="host-entry-price"
                type="number"
                placeholder={`Entry price (${hostForm.currency})`}
                value={hostForm.entry_price}
                onChange={(e) => setHostForm(f => ({ ...f, entry_price: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
              />
              <Input
                data-testid="host-prize-value"
                type="number"
                placeholder={`Prize value (${hostForm.currency})`}
                value={hostForm.prize_value_xrp}
                onChange={(e) => setHostForm(f => ({ ...f, prize_value_xrp: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
              />
            </div>
            {(hostApprox(hostForm.entry_price) || hostApprox(hostForm.prize_value_xrp)) && (
              <div className="flex justify-between text-[10px] text-amber-300/90 px-1">
                <span data-testid="entry-price-approx">{hostApprox(hostForm.entry_price) ? `Entry ${hostApprox(hostForm.entry_price)}` : ''}</span>
                <span data-testid="prize-value-approx">{hostApprox(hostForm.prize_value_xrp) ? `Prize ${hostApprox(hostForm.prize_value_xrp)}` : ''}</span>
              </div>
            )}
            {hostForm.prize_type === 'nft' && (
              <div className="space-y-1.5">
                <Button
                  data-testid="host-pick-from-wallet"
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowHostNftPicker(true)}
                  className="w-full border-purple-500/40 text-purple-300 hover:bg-purple-500/10 h-9 text-xs"
                >
                  <Wallet className="w-3 h-3 mr-1.5" /> Pick from my wallet
                </Button>
                <div className="flex gap-2">
                  <Input
                    data-testid="host-nft-token"
                    placeholder="NFT Token ID (or paste manually)"
                    value={hostForm.nft_token_id}
                    onChange={(e) => { setHostForm(f => ({ ...f, nft_token_id: e.target.value })); setHostNftAnalysis(null); }}
                    className="bg-slate-800 border-slate-700 text-white h-10 text-sm flex-1 font-mono"
                  />
                  <Button
                    data-testid="host-verify-nft-button"
                    type="button"
                    size="sm"
                    onClick={() => handleVerifyNft(hostForm.nft_token_id, 'host')}
                    disabled={verifyingNft || !hostForm.nft_token_id}
                    className="bg-emerald-600 hover:bg-emerald-700 h-10 text-xs px-3"
                  >
                    {verifyingNft ? '...' : 'Verify'}
                  </Button>
                </div>
                {hostNftAnalysis && <NftTrustBadge analysis={hostNftAnalysis} />}
                {hostNftAnalysis && hostNftAnalysis.risk_flags?.length > 0 && (
                  <ul className="text-[10px] text-slate-400 space-y-0.5 pl-1">
                    {hostNftAnalysis.risk_flags.map((f) => (
                      <li key={f}>• {f.replace(/_/g, ' ')}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {hostForm.prize_type === 'physical' && (
              <>
                <Input
                  data-testid="host-shipping-notes"
                  placeholder="Shipping notes (e.g. signature required)"
                  value={hostForm.physical_shipping_notes}
                  onChange={(e) => setHostForm(f => ({ ...f, physical_shipping_notes: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
                />
                <p className="text-[10px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2.5 py-1.5">
                  📦 Physical prizes MUST ship signature-required. Shipping without a signature requirement makes the sale final — you carry the non-delivery risk.
                </p>
              </>
            )}
            {/* Minimum players */}
            <div className="space-y-1">
              <Select
                value={String(hostForm.min_players)}
                onValueChange={(v) => setHostForm(f => ({ ...f, min_players: parseInt(v, 10) }))}
              >
                <SelectTrigger data-testid="host-min-players" className="bg-slate-800 border-slate-700 text-white h-10 text-sm">
                  <SelectValue placeholder="Minimum players" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="1" className="text-white">1 player (no minimum)</SelectItem>
                  <SelectItem value="3" className="text-white">3 players minimum (recommended)</SelectItem>
                  <SelectItem value="5" className="text-white">5 players minimum</SelectItem>
                  <SelectItem value="10" className="text-white">10 players (full pool)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[9px] text-slate-500 pl-1">
                {hostForm.min_players === 1
                  ? 'Draw runs even with 1 player'
                  : `Draw auto-refunds all buyers if fewer than ${hostForm.min_players} players join. Keeps your listing fair & buyers protected.`}
              </p>
            </div>
            {/* Custom Buy-It-Now price (optional) */}
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Input
                  data-testid="host-bin-price"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder={`BIN price (optional — default: ${hostForm.entry_price ? (parseFloat(hostForm.entry_price) * (1 + feeConfig.host_fee_pct + feeConfig.hosted_platform_fee_pct) * 10).toFixed(1) : '10×'} ${hostForm.currency})`}
                  value={hostForm.bin_price}
                  onChange={(e) => setHostForm(f => ({ ...f, bin_price: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
                />
              </div>
              <p className="text-[9px] text-slate-500 pl-1">
                Buy-It-Now price = what a buyer pays to instantly win, no draw. Leave blank for the default 10× per-slot cost.
                {hostForm.entry_price && hostForm.prize_value_xrp && (
                  <span className="block text-amber-300/70 pt-0.5">
                    Allowed: {(parseFloat(hostForm.entry_price) * (1 + feeConfig.host_fee_pct + feeConfig.hosted_platform_fee_pct)).toFixed(2)}–{(parseFloat(hostForm.prize_value_xrp) * 5).toFixed(0)} {hostForm.currency}
                  </span>
                )}
              </p>
            </div>
            {/* Authenticity & Photos */}
            <AuthenticityFields
              condition={hostForm.condition}
              grade={hostForm.grade}
              certNumber={hostForm.cert_number}
              onChange={(patch) => setHostForm(f => ({ ...f, ...patch }))}
              photoFiles={photoFiles}
              setPhotoFiles={setPhotoFiles}
              photoUrls={photoUrls}
              setPhotoUrls={setPhotoUrls}
              certVerified={certVerified}
              setCertVerified={setCertVerified}
              required={hostForm.prize_type === 'physical'}
            />
            {hostForm.entry_price && (
              <div className="bg-slate-800/50 rounded-xl p-3 border border-slate-700/50">
                <p className="text-[10px] text-slate-500 mb-1.5">Fee Breakdown</p>
                <div className="space-y-1 text-[11px]">
                  <div className="flex justify-between">
                    <span className="text-slate-400">You receive per slot sold</span>
                    <span className="text-emerald-400 font-semibold">{(parseFloat(hostForm.entry_price || 0) * 0.9411).toFixed(2)} {hostForm.currency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Platform fee (from pool)</span>
                    <span className="text-slate-300">5.89%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Buyer fees</span>
                    <span data-testid="fee-waived-label" className="text-emerald-300 font-bold">None — 0% 🎉</span>
                  </div>
                  <div className="flex justify-between pt-1 border-t border-slate-700/50">
                    <span className="text-slate-300 font-medium">Total player pays</span>
                    <span className="text-orange-300 font-semibold">{parseFloat(hostForm.entry_price || 0).toFixed(2)} {hostForm.currency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">If all 10 slots sell</span>
                    <span className="text-emerald-300 font-bold">{(parseFloat(hostForm.entry_price || 0) * 0.9411 * 10).toFixed(2)} {hostForm.currency} to you</span>
                  </div>
                  {hostApprox(parseFloat(hostForm.entry_price || 0) * 0.9411 * 10) && (
                    <div className="flex justify-between">
                      <span className="text-slate-500"></span>
                      <span data-testid="pool-approx" className="text-amber-300/80 text-[10px]">{hostApprox(parseFloat(hostForm.entry_price || 0) * 0.9411 * 10)} at live DEX rate</span>
                    </div>
                  )}
                  <p className="text-[9px] text-slate-500 pt-1">Winner gets your prize — you keep the pool. Physical prize proceeds release from escrow after confirmed delivery.</p>
                </div>
              </div>
            )}
            <Button
              data-testid="host-submit-button"
              onClick={handleHostGame}
              disabled={actionLoading || uploadingPhotos}
              className="w-full bg-purple-600 hover:bg-purple-700 h-11 font-semibold"
            >
              {uploadingPhotos ? 'Uploading photos...' : actionLoading ? 'Creating...' : 'List Prize Room'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auction Creation Dialog */}
      <Dialog open={showAuctionDialog} onOpenChange={setShowAuctionDialog}>
        <DialogContent data-testid="auction-create-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <Gavel className="w-5 h-5 text-purple-400" /> Create Auction
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              List your NFT or collectible. Highest bidder wins. Anti-snipe extends bids placed in the final 2 min.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              data-testid="auction-room-name"
              placeholder="Auction title"
              value={auctionForm.room_name}
              onChange={(e) => setAuctionForm(f => ({ ...f, room_name: e.target.value }))}
              className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
            />
            <Select value={auctionForm.prize_type} onValueChange={(v) => setAuctionForm(f => ({ ...f, prize_type: v }))}>
              <SelectTrigger data-testid="auction-prize-type" className="bg-slate-800 border-slate-700 text-white h-10 text-sm">
                <SelectValue placeholder="Prize type" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="nft" className="text-white">NFT</SelectItem>
                <SelectItem value="physical" className="text-white">Physical Item</SelectItem>
              </SelectContent>
            </Select>
            <Input
              data-testid="auction-prize-description"
              placeholder="Description"
              value={auctionForm.prize_description}
              onChange={(e) => setAuctionForm(f => ({ ...f, prize_description: e.target.value }))}
              className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                data-testid="auction-starting-bid"
                type="number"
                step="0.1"
                placeholder="Starting bid (XRP)"
                value={auctionForm.starting_bid}
                onChange={(e) => setAuctionForm(f => ({ ...f, starting_bid: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
              />
              <Input
                data-testid="auction-prize-value"
                type="number"
                step="0.1"
                placeholder="Est. value (XRP)"
                value={auctionForm.prize_value_xrp}
                onChange={(e) => setAuctionForm(f => ({ ...f, prize_value_xrp: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                data-testid="auction-reserve-price"
                type="number"
                step="0.1"
                placeholder="Reserve (optional)"
                value={auctionForm.reserve_price}
                onChange={(e) => setAuctionForm(f => ({ ...f, reserve_price: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
              />
              <Input
                data-testid="auction-bid-increment"
                type="number"
                step="0.1"
                placeholder="Min increment"
                value={auctionForm.bid_increment}
                onChange={(e) => setAuctionForm(f => ({ ...f, bid_increment: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
              />
            </div>
            <Select
              value={String(auctionForm.duration_hours)}
              onValueChange={(v) => setAuctionForm(f => ({ ...f, duration_hours: parseInt(v, 10) }))}
            >
              <SelectTrigger data-testid="auction-duration" className="bg-slate-800 border-slate-700 text-white h-10 text-sm">
                <SelectValue placeholder="Duration" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="24" className="text-white">24 hours</SelectItem>
                <SelectItem value="48" className="text-white">48 hours</SelectItem>
                <SelectItem value="72" className="text-white">72 hours</SelectItem>
              </SelectContent>
            </Select>
            {auctionForm.prize_type === 'nft' && (
              <div className="space-y-1.5">
                <Button
                  data-testid="auction-pick-from-wallet"
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowAuctionNftPicker(true)}
                  className="w-full border-purple-500/40 text-purple-300 hover:bg-purple-500/10 h-9 text-xs"
                >
                  <Wallet className="w-3 h-3 mr-1.5" /> Pick from my wallet
                </Button>
                <div className="flex gap-2">
                  <Input
                    data-testid="auction-nft-token"
                    placeholder="NFT Token ID (or paste manually)"
                    value={auctionForm.nft_token_id}
                    onChange={(e) => { setAuctionForm(f => ({ ...f, nft_token_id: e.target.value })); setAuctionNftAnalysis(null); }}
                    className="bg-slate-800 border-slate-700 text-white h-10 text-sm flex-1 font-mono"
                  />
                  <Button
                    data-testid="auction-verify-nft-button"
                    type="button"
                    size="sm"
                    onClick={() => handleVerifyNft(auctionForm.nft_token_id, 'auction')}
                    disabled={verifyingNft || !auctionForm.nft_token_id}
                    className="bg-emerald-600 hover:bg-emerald-700 h-10 text-xs px-3"
                  >
                    {verifyingNft ? '...' : 'Verify'}
                  </Button>
                </div>
                {auctionNftAnalysis && <NftTrustBadge analysis={auctionNftAnalysis} />}
              </div>
            )}
            {auctionForm.prize_type === 'physical' && (
              <Input
                data-testid="auction-shipping-notes"
                placeholder="Shipping notes"
                value={auctionForm.physical_shipping_notes}
                onChange={(e) => setAuctionForm(f => ({ ...f, physical_shipping_notes: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white h-10 text-sm"
              />
            )}
            <AuthenticityFields
              condition={auctionForm.condition}
              grade={auctionForm.grade}
              certNumber={auctionForm.cert_number}
              onChange={(patch) => setAuctionForm(f => ({ ...f, ...patch }))}
              photoFiles={auctionPhotoFiles}
              setPhotoFiles={setAuctionPhotoFiles}
              photoUrls={auctionPhotoUrls}
              setPhotoUrls={setAuctionPhotoUrls}
              certVerified={auctionCertVerified}
              setCertVerified={setAuctionCertVerified}
              required={auctionForm.prize_type === 'physical'}
            />
            <Button
              data-testid="auction-submit-button"
              onClick={handleCreateAuction}
              disabled={actionLoading}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 h-11 font-semibold"
            >
              {actionLoading ? 'Launching auction...' : 'Launch Auction'}
            </Button>
            <p className="text-[9px] text-slate-600 text-center">
              Bidder funds are held in escrow until outbid or auction ends.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deposit Dialog - Real XRPL Testnet */}
      <Dialog open={showDepositDialog} onOpenChange={setShowDepositDialog}>
        <DialogContent data-testid="deposit-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Deposit XRP</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Send XRP to the platform wallet with your unique destination tag.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {depositLoading ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-2"></div>
                <p className="text-xs text-slate-400">Loading deposit info...</p>
              </div>
            ) : depositInfo ? (
              <>
                {/* Network-aware safety gate — must be acknowledged before address/QR are revealed */}
                {!depositTestnetAck ? (
                  depositInfo.network === 'mainnet' ? (
                    <div data-testid="deposit-mainnet-warning" className="bg-red-500/10 border-2 border-red-500/50 rounded-xl p-3.5 space-y-2.5">
                      <div className="flex items-start gap-2">
                        <span className="text-xl leading-none">🛡️</span>
                        <div className="space-y-1">
                          <p className="text-sm font-bold text-red-300 leading-tight">MAINNET — REAL XRP</p>
                          <p className="text-[11px] text-red-100/90 leading-relaxed">
                            This wallet accepts <strong>real XRPL Mainnet XRP</strong>. Transfers are
                            <strong> irreversible</strong>. Triple-check the address before sending.
                          </p>
                          <p className="text-[10px] text-red-300/90 pt-0.5">
                            In Xaman, the top of the screen must say <strong>MAINNET</strong> (not Testnet).
                            Always include your unique <strong>destination tag</strong> shown below.
                          </p>
                        </div>
                      </div>
                      <label className="flex items-start gap-2 cursor-pointer select-none pt-1">
                        <input
                          data-testid="deposit-mainnet-ack-checkbox"
                          type="checkbox"
                          onChange={(e) => setDepositTestnetAck(e.target.checked)}
                          className="w-3.5 h-3.5 accent-red-500 mt-0.5 flex-shrink-0"
                        />
                        <span className="text-[11px] text-red-100/90">
                          I understand this is <strong>real XRP</strong> and I've verified the address + destination tag.
                        </span>
                      </label>
                    </div>
                  ) : (
                    <div data-testid="deposit-testnet-warning" className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-3.5 space-y-2.5">
                      <div className="flex items-start gap-2">
                        <span className="text-xl leading-none">⚠️</span>
                        <div className="space-y-1">
                          <p className="text-sm font-bold text-amber-300 leading-tight">TESTNET ONLY</p>
                          <p className="text-[11px] text-amber-100/90 leading-relaxed">
                            This wallet only accepts <strong>XRPL Testnet</strong> XRP (free, from
                            a <a className="underline" href="https://test.bithomp.com/faucet" target="_blank" rel="noopener noreferrer">testnet faucet</a>).
                            Do <strong>NOT</strong> send real Mainnet XRP — it would land in a different account we don't yet control,
                            and would be locked until our Mainnet migration.
                          </p>
                          <p className="text-[10px] text-amber-300/80 pt-0.5">
                            In Xaman, check the network indicator at the very top of the screen — it must say <strong>TESTNET</strong>.
                          </p>
                        </div>
                      </div>
                      <label className="flex items-start gap-2 cursor-pointer select-none pt-1">
                        <input
                          data-testid="deposit-testnet-ack-checkbox"
                          type="checkbox"
                          onChange={(e) => setDepositTestnetAck(e.target.checked)}
                          className="w-3.5 h-3.5 accent-amber-500 mt-0.5 flex-shrink-0"
                        />
                        <span className="text-[11px] text-amber-100/90">
                          I confirm I'm sending from a <strong>Testnet</strong> wallet, not Mainnet.
                        </span>
                      </label>
                    </div>
                  )
                ) : null}

                <div className={`bg-slate-800/50 rounded-xl p-3 border border-slate-700 ${!depositTestnetAck ? 'opacity-40 pointer-events-none select-none blur-sm' : ''}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-[10px] text-slate-500">Platform Wallet ({depositInfo.network === 'mainnet' ? 'Mainnet' : 'Testnet'})</p>
                    <button
                      data-testid="copy-platform-address"
                      onClick={() => {
                        navigator.clipboard.writeText(depositInfo.platform_address)
                          .then(() => { haptic('notification','success'); toast.success('Address copied'); })
                          .catch(() => toast.error('Could not copy'));
                      }}
                      className="inline-flex items-center gap-1 text-[10px] text-emerald-300 hover:text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-md px-2 py-1 transition"
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                  <p data-testid="platform-address" className="text-xs text-emerald-300 font-mono break-all select-all">{depositInfo.platform_address}</p>

                  {/* Inline QR for one-tap wallet scan */}
                  <div className="mt-3 bg-white rounded-lg p-2 flex flex-col items-center">
                    <QRCodeSVG
                      data-testid="deposit-qr"
                      value={`https://xumm.app/detect/request:${depositInfo.platform_address}?dt=${depositInfo.destination_tag}`}
                      size={140}
                      level="M"
                      includeMargin={false}
                    />
                    <p className="text-[9px] text-slate-700 mt-1.5">Scan in Xaman — address & tag auto-filled</p>
                  </div>

                  {/* One-tap Xaman deeplink — opens Xaman with address + tag prefilled */}
                  <a
                    data-testid="deposit-open-xaman"
                    href={`https://xumm.app/detect/request:${depositInfo.platform_address}?dt=${depositInfo.destination_tag}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => haptic('impact','light')}
                    className="mt-3 block w-full text-center bg-blue-500 hover:bg-blue-400 text-white font-bold py-2.5 rounded-md text-sm transition"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Wallet className="w-4 h-4" /> Open in Xaman
                    </span>
                  </a>
                </div>
                <div className="bg-orange-500/10 rounded-xl p-3 border border-orange-500/20">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-[10px] text-slate-500">Your Destination Tag (REQUIRED)</p>
                    <button
                      data-testid="copy-destination-tag"
                      onClick={() => {
                        navigator.clipboard.writeText(String(depositInfo.destination_tag))
                          .then(() => { haptic('notification','success'); toast.success('Tag copied'); })
                          .catch(() => toast.error('Could not copy'));
                      }}
                      className="inline-flex items-center gap-1 text-[10px] text-orange-300 hover:text-orange-200 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 rounded-md px-2 py-1 transition"
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                  <p data-testid="destination-tag" className="text-2xl font-bold text-orange-300 font-mono select-all">{depositInfo.destination_tag}</p>
                  <p className="text-[10px] text-red-400 mt-1 font-medium">Always include this tag or your deposit will be lost!</p>
                </div>
                <div className="bg-slate-800/30 rounded-xl p-3 border border-slate-700/40">
                  <p className="text-[11px] text-slate-400 leading-relaxed">
                    1. Open your XRP wallet (Xaman, Trust Wallet, etc.)<br/>
                    2. Send any amount of XRP to the address above<br/>
                    3. Include destination tag: <span className="text-orange-300 font-bold">{depositInfo.destination_tag}</span><br/>
                    4. Deposits are auto-detected within ~30 seconds
                  </p>
                </div>
                <div className="bg-amber-500/5 rounded-xl p-3 border border-amber-500/20">
                  <p className="text-[11px] text-amber-200/90 leading-relaxed">
                    🍯 <span className="font-bold">FUZZY deposits</span> use the SAME address &amp; tag. Send FUZZY (FuzzyXRP) and it lands in your FUZZY balance automatically.
                  </p>
                </div>
                <div className="text-[10px] text-center text-slate-600 flex items-center justify-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  Network: XRPL {depositInfo.network === 'mainnet' ? 'Mainnet' : 'Testnet'}
                </div>
                {recentDeposits.length > 0 && (
                  <div className="pt-2">
                    <p className="text-[10px] text-slate-500 mb-1.5">Recent Deposits</p>
                    {recentDeposits.map((d, i) => (
                      <div key={d.tx_hash || `dep-${i}`} className="flex justify-between items-center bg-slate-800/30 rounded-lg px-2.5 py-1.5 mb-1 text-[11px]">
                        <span className="text-emerald-400 font-semibold">+{d.amount_xrp?.toFixed(2)} {d.currency === 'FUZZY' ? 'FUZZY' : 'XRP'}</span>
                        <span className="text-slate-500 font-mono">{d.tx_hash?.slice(0, 10)}...</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4 text-sm text-red-400">Failed to load deposit info. Try again.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Withdraw Dialog - Real XRPL Testnet */}
      <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
        <DialogContent data-testid="withdraw-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Withdraw {withdrawCurrency}</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Send {withdrawCurrency} from your balance to your linked wallet</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {(user?.fuzzy_balance || 0) > 0 && (
              <div className="flex gap-2">
                {['XRP', 'FUZZY'].map((c) => (
                  <button
                    key={c}
                    data-testid={`withdraw-currency-${c.toLowerCase()}`}
                    onClick={() => setWithdrawCurrency(c)}
                    className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-all ${
                      withdrawCurrency === c
                        ? (c === 'FUZZY' ? 'bg-amber-500/20 border-amber-500/50 text-amber-300' : 'bg-orange-500/20 border-orange-500/50 text-orange-300')
                        : 'bg-slate-800 border-slate-700 text-slate-400'
                    }`}
                  >
                    {c === 'FUZZY' ? '🍯 FUZZY' : 'XRP'}
                  </button>
                ))}
              </div>
            )}
            <div className="bg-slate-800/50 rounded-lg p-2.5 border border-slate-700">
              <p className="text-[10px] text-slate-500">Available</p>
              <p className="text-lg font-bold text-orange-300">
                {withdrawCurrency === 'FUZZY'
                  ? `${(user?.fuzzy_balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} FUZZY`
                  : `${(user?.balance || 0).toFixed(2)} XRP`}
              </p>
            </div>
            {withdrawCurrency === 'FUZZY' && (
              <p className="text-[10px] text-amber-300/80">Destination wallet must hold a FUZZY trustline to receive.</p>
            )}
            {user?.xrpl_address ? (
              <div className="bg-emerald-500/5 rounded-lg p-2.5 border border-emerald-500/20">
                <p className="text-[10px] text-slate-500">Sending to</p>
                <p data-testid="withdraw-destination-preview" className="text-xs text-emerald-300 font-mono break-all">
                  {withdrawUseAlt
                    ? (withdrawSavedId
                        ? (() => {
                            const a = savedAddresses.find((x) => x.entry_id === withdrawSavedId);
                            return a ? `${a.label} · ${a.address}` : 'Pick an address';
                          })()
                        : (withdrawAltAddress || 'Enter address below'))
                    : user.xrpl_address}
                </p>
              </div>
            ) : (
              <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/30 space-y-2">
                <div className="flex items-start gap-2">
                  <Wallet className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-red-300">
                    No wallet linked yet. Connect Xaman, or send to a one-time address below.
                  </p>
                </div>
                <Button
                  data-testid="withdraw-connect-xaman"
                  onClick={() => { setShowWithdrawDialog(false); setShowXamanDialog(true); }}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold h-9 text-xs"
                >
                  <Wallet className="w-3.5 h-3.5 mr-1.5" /> Connect with Xaman
                </Button>
              </div>
            )}

            {/* Alt-destination toggle */}
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                data-testid="withdraw-alt-toggle"
                type="checkbox"
                checked={withdrawUseAlt}
                onChange={(e) => {
                  setWithdrawUseAlt(e.target.checked);
                  if (!e.target.checked) { setWithdrawSavedId(''); setWithdrawSaveNew(false); }
                }}
                className="w-3.5 h-3.5 accent-amber-500"
              />
              <span className="text-[11px] text-slate-300">
                Send to a different address (cold storage, exchange, friend…)
              </span>
            </label>
            {withdrawUseAlt && (
              <div className="space-y-2">
                {/* Saved addresses picker */}
                {savedAddresses.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide">Pick from address book</p>
                    <Select
                      value={withdrawSavedId || 'manual'}
                      onValueChange={(v) => {
                        if (v === 'manual') {
                          setWithdrawSavedId('');
                        } else {
                          setWithdrawSavedId(v);
                          setWithdrawAltAddress('');
                          setWithdrawSaveNew(false);
                        }
                      }}
                    >
                      <SelectTrigger data-testid="withdraw-saved-select" className="bg-slate-800 border-slate-700 text-white h-10 text-sm">
                        <SelectValue placeholder="Type a new address" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="manual" className="text-slate-300">
                          ✏️ Type a new address
                        </SelectItem>
                        {savedAddresses.map((a) => (
                          <SelectItem
                            key={a.entry_id}
                            value={a.entry_id}
                            data-testid={`withdraw-saved-option-${a.entry_id.slice(0, 8)}`}
                            className="text-emerald-300"
                          >
                            {a.label} — {a.address.slice(0, 8)}…{a.address.slice(-4)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Manual address input — shown when no saved selected */}
                {!withdrawSavedId && (
                  <div className="space-y-1.5">
                    <Input
                      data-testid="withdraw-alt-address"
                      placeholder="rXXXXX..."
                      value={withdrawAltAddress}
                      onChange={(e) => setWithdrawAltAddress(e.target.value.trim())}
                      className="bg-slate-800 border-amber-500/40 text-white h-10 text-sm font-mono"
                    />
                    <p className="text-[10px] text-amber-300/80">
                      ⚠️ Double-check the address. Crypto transfers are irreversible.
                    </p>
                    {/* Save-this-address toggle */}
                    <label className="flex items-center gap-2 cursor-pointer select-none pt-1">
                      <input
                        data-testid="withdraw-save-toggle"
                        type="checkbox"
                        checked={withdrawSaveNew}
                        onChange={(e) => setWithdrawSaveNew(e.target.checked)}
                        className="w-3.5 h-3.5 accent-emerald-500"
                      />
                      <span className="text-[11px] text-emerald-300">Save this address to my Address Book</span>
                    </label>
                    {withdrawSaveNew && (
                      <Input
                        data-testid="withdraw-save-label"
                        autoFocus
                        placeholder='Label (e.g. "Cold Wallet")'
                        value={withdrawSaveLabel}
                        maxLength={40}
                        onChange={(e) => setWithdrawSaveLabel(e.target.value)}
                        className="bg-slate-800 border-emerald-500/40 text-white h-9 text-sm"
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            <Input data-testid="withdraw-amount-input" type="number" placeholder={`Amount (${withdrawCurrency})`} value={withdrawAmount} onChange={(e) => setWithdrawAmount(e.target.value)} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
            <Button
              data-testid="withdraw-submit-button"
              onClick={handleWithdraw}
              disabled={actionLoading || (!user?.xrpl_address && !withdrawUseAlt)}
              className="w-full bg-indigo-600 hover:bg-indigo-700 h-10"
            >
              {actionLoading ? 'Sending on XRPL...' : (withdrawUseAlt ? (withdrawSavedId ? 'Withdraw to Saved Address' : 'Withdraw to Custom Address') : 'Withdraw to Wallet')}
            </Button>
            <p className="text-[10px] text-center text-slate-600">Withdrawal is sent as a real XRPL transaction</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link Wallet Dialog */}
      <Dialog open={showWalletLinkDialog} onOpenChange={setShowWalletLinkDialog}>
        <DialogContent data-testid="wallet-link-dialog" className="bg-slate-900 border-slate-700 max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Link XRP Wallet</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Link your wallet for on-chain transactions</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {user?.xrpl_address && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5">
                <p className="text-[10px] text-slate-400 mb-0.5">Linked</p>
                <p className="text-xs text-emerald-300 font-mono break-all">{user.xrpl_address}</p>
              </div>
            )}

            {/* PRIMARY: One-tap Xaman Sign-In */}
            <Button
              data-testid="open-xaman-connect"
              onClick={() => { setShowWalletLinkDialog(false); setShowXamanDialog(true); }}
              className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold h-11"
            >
              <Wallet className="w-4 h-4 mr-2" /> Connect with Xaman
            </Button>
            <p className="text-[10px] text-slate-500 text-center -mt-1">
              Recommended · one tap, no fees, no XRP needed
            </p>

            {/* Divider */}
            <div className="flex items-center gap-2 py-1">
              <div className="flex-1 h-px bg-slate-700/50" />
              <span className="text-[9px] text-slate-500 uppercase tracking-wider">Or paste manually</span>
              <div className="flex-1 h-px bg-slate-700/50" />
            </div>

            {/* FALLBACK: manual paste */}
            <Input data-testid="wallet-link-input" placeholder="rXXXXX..." value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)} className="bg-slate-800 border-slate-700 text-white h-10 text-sm" />
            <Button data-testid="wallet-link-submit" onClick={handleLinkWallet} disabled={actionLoading} variant="outline" className="w-full border-slate-600 text-slate-300 hover:bg-slate-800 h-10">
              {actionLoading ? 'Linking...' : 'Link Manually'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* NFT Picker dialogs (wallet → auto-fill) */}
      <NftPickerDialog
        open={showHostNftPicker}
        onOpenChange={setShowHostNftPicker}
        hasWallet={!!user?.xrpl_address}
        onSelect={(nft) => {
          setHostForm((f) => ({
            ...f,
            nft_token_id: nft.nft_token_id,
            prize_description: nft.description
              ? `${nft.name ? nft.name + ' — ' : ''}${nft.description}`.slice(0, 280)
              : (nft.name || f.prize_description),
            room_name: f.room_name || nft.name || '',
          }));
          if (nft.image) setPhotoUrls((u) => u.includes(nft.image) ? u : [...u, nft.image]);
          setHostNftAnalysis(null);
        }}
      />
      <NftPickerDialog
        open={showAuctionNftPicker}
        onOpenChange={setShowAuctionNftPicker}
        hasWallet={!!user?.xrpl_address}
        onSelect={(nft) => {
          setAuctionForm((f) => ({
            ...f,
            nft_token_id: nft.nft_token_id,
            prize_description: nft.description
              ? `${nft.name ? nft.name + ' — ' : ''}${nft.description}`.slice(0, 280)
              : (nft.name || f.prize_description),
            room_name: f.room_name || nft.name || '',
          }));
          if (nft.image) setAuctionPhotoUrls((u) => u.includes(nft.image) ? u : [...u, nft.image]);
          setAuctionNftAnalysis(null);
        }}
      />

      {/* Wallet Claim Dialog (proof-of-ownership for stuck wallets) */}
      <ClaimWalletDialog
        open={showClaimDialog}
        onOpenChange={setShowClaimDialog}
        xrplAddress={claimAddress}
        onSuccess={(addr) => {
          toast.success('Wallet claimed and linked to your account!');
          // Refresh user profile so xrpl_address is reflected app-wide
          axios.get(`${API}/auth/me`).catch(() => {});
          setWalletAddress('');
          setClaimAddress('');
        }}
      />

      {/* Xaman One-Tap Connect (primary wallet linking method) */}
      <XamanConnectDialog
        open={showXamanDialog}
        onOpenChange={setShowXamanDialog}
        onSuccess={() => {
          axios.get(`${API}/auth/me`).catch(() => {});
          setWalletAddress('');
        }}
      />

      {/* Quick BIN from Home feed */}
      <Dialog open={!!quickBinGame} onOpenChange={(open) => { if (!open) setQuickBinGame(null); }}>
        <DialogContent data-testid="home-bin-dialog" className="bg-slate-900 border-slate-700 w-[calc(100%-2rem)] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl text-white">Buy It Now</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Instantly win the prize — skip the draw
            </DialogDescription>
          </DialogHeader>
          {quickBinGame && (() => {
            const binPrice = quickBinGame.bin_price ?? quickBinGame.total_cost * 10;
            const balance = user?.balance || 0;
            return (
              <div className="space-y-3 pt-2">
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3.5 text-center">
                  <p className="text-[10px] text-slate-500 mb-1">BIN Price (all 10 slots)</p>
                  <p className="text-2xl font-black text-orange-400">{binPrice.toFixed(1)} XRP</p>
                  {quickBinGame.bin_price != null && (
                    <p className="text-[9px] text-amber-300/70 mt-0.5">Host-set price</p>
                  )}
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Room</span>
                    <span className="text-white font-medium truncate ml-2">{quickBinGame.room_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Prize</span>
                    <span className="text-white font-medium text-right truncate ml-2 max-w-[60%]">{quickBinGame.prize_description}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Type</span>
                    <span className="text-purple-300 uppercase text-xs font-semibold">{quickBinGame.prize_type}</span>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs mb-1.5">Draw</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        data-testid="home-bin-draw-midday"
                        onClick={() => setQuickBinDrawType('midday')}
                        className={`py-1.5 rounded-lg text-xs font-semibold transition ${
                          quickBinDrawType === 'midday'
                            ? 'bg-gradient-to-r from-amber-500/30 to-orange-500/30 text-amber-200 border border-amber-500/40'
                            : 'bg-slate-700/40 text-slate-400 border border-slate-700 hover:bg-slate-700/60'
                        }`}
                      >
                        Midday
                      </button>
                      <button
                        data-testid="home-bin-draw-evening"
                        onClick={() => setQuickBinDrawType('evening')}
                        className={`py-1.5 rounded-lg text-xs font-semibold transition ${
                          quickBinDrawType === 'evening'
                            ? 'bg-gradient-to-r from-indigo-500/30 to-purple-500/30 text-indigo-200 border border-indigo-500/40'
                            : 'bg-slate-700/40 text-slate-400 border border-slate-700 hover:bg-slate-700/60'
                        }`}
                      >
                        Evening
                      </button>
                    </div>
                  </div>
                  <div className="h-px bg-slate-700"></div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Your balance</span>
                    <span className="text-slate-300">{balance.toFixed(2)} XRP</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">After BIN</span>
                    <span className={`font-medium ${balance >= binPrice ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(balance - binPrice).toFixed(2)} XRP
                    </span>
                  </div>
                </div>
                {balance < binPrice && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2.5 text-xs text-red-400">
                    Insufficient balance. You need {binPrice.toFixed(1)} XRP.
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    data-testid="home-bin-view-room"
                    onClick={() => { const g = quickBinGame; setQuickBinGame(null); navigate(`/game/${g.game_id}`); }}
                    variant="outline"
                    className="flex-1 h-11 text-sm border-slate-700 bg-slate-800/50 hover:bg-slate-800 text-slate-200"
                  >
                    View room
                  </Button>
                  <Button
                    data-testid="home-bin-confirm"
                    onClick={handleQuickBin}
                    disabled={quickBinLoading || balance < binPrice}
                    className="flex-[2] bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 h-11 font-bold text-base"
                  >
                    {quickBinLoading ? 'Processing…' : 'Confirm BIN'}
                  </Button>
                </div>
                <p className="text-[9px] text-slate-600 text-center">
                  Delivery via {quickBinGame.prize_type === 'physical' ? 'escrow-protected shipping' : 'on-chain NFT transfer'}
                </p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WalletCard({ name, desc, tag, tagColor }) {
  return (
    <div className="flex items-center gap-3 bg-slate-800/40 border border-slate-700/40 rounded-xl p-3">
      <div className="w-9 h-9 rounded-lg bg-slate-700/50 flex items-center justify-center flex-shrink-0">
        <CreditCard className="w-4 h-4 text-slate-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-white">{name}</p>
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold border ${tagColor}`}>{tag}</span>
        </div>
        <p className="text-[10px] text-slate-500 mt-0.5">{desc}</p>
      </div>
    </div>
  );
}

export default Home;
